import { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import ContentCard from '../../../../../components/layout/ContentCard/ContentCard';
import Button from '../../../../../components/common/Button/Button';
import Badge from '../../../../../components/common/Badge/Badge';
import Input from '../../../../../components/common/Input/Input';
import { translate, verifyHideCountryItems, formatter } from '../../../../../utils/Common';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useClient } from '../../../../../hooks/useClient';
import * as FaIcons from 'react-icons/fa';
import { RiMoneyDollarCircleFill } from 'react-icons/ri';
import { GiPlatform } from 'react-icons/gi';
import { PiLockersFill } from "react-icons/pi";
import { IoSearchOutline } from "react-icons/io5";
import { MdDeleteForever } from "react-icons/md";
import PlatformSearchList from '../../../../../components/PlatformSearchList';
import Spinner from '../../../../../components/Spinner';
import ModalContent from '../../../../../components/ModalContent';
import styles from '../../ClientDetail.module.css';
import { verifyAccessViewPermissions } from "../../../../../config/permissions";

const PlatformInfoTab = ({ client, onUpdateClient, onReloadClient, clientId, canUpdate = true }) => {
  const location = useLocation();
  const permissionsPath = "/dashboard/clients";
  const canUpdatePermission = verifyAccessViewPermissions(["update"], {
    path: permissionsPath || location.pathname,
  });
  const canUpdateEffective = Boolean(canUpdate) && canUpdatePermission;
  
  const { getPlatformsAsignedClient, saveAssignedPlatform, saveDeallocatePlatform } = useClient();
  const [isEditingPrimary, setIsEditingPrimary] = useState(false);
  const [isEditingSecondary, setIsEditingSecondary] = useState(false);
  const [platformSelect, setPlatformSelect] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isBoxit, setIsBoxit] = useState(false);
  const { register, handleSubmit, errors, setValue } = useForm();

  // Lockers assigned states
  const { IdCliente } = client || {};
  const [lockersData, setLockersData] = useState([]);
  const [lockersEditMode, setLockersEditMode] = useState(false);
  const [lockersPlatformSelect, setLockersPlatformSelect] = useState(null);
  const [lockersDataFiltered, setLockersDataFiltered] = useState([]);
  const [lockersSearchValue, setLockersSearchValue] = useState('');
  const [deletingPlatformId, setDeletingPlatformId] = useState(null);
  const [idsHide, setIdsHide] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modal, setModal] = useState({});
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [lockersIsLoading, setLockersIsLoading] = useState({
    save: false,
    delete: false,
    // Starts true: the list is empty until the first fetch answers, and a false
    // flag paints the "no lockers assigned" state for a frame before the data
    // lands — the screen appears to find nothing and then change its mind.
    data: true,
  });

  // Lockers assigned functions
  useEffect(() => {
    if (IdCliente) {
      getLockersData();
    }
  }, [IdCliente]);

  const getLockersData = async () => {
    setLockersIsLoading({ ...lockersIsLoading, data: true });
    const signal = new AbortController().signal;
    const response = await getPlatformsAsignedClient(signal, { clientId: IdCliente });
    const idsAssign = [...new Set(response.map(platform => platform?.IdPlatform))];
    setIdsHide(idsAssign);
    setLockersData(response);
    setLockersDataFiltered(response);
    setLockersIsLoading({ ...lockersIsLoading, data: false });
  };

  const handleDeletePlatform = async (idPlatform) => {
    if (!canUpdateEffective) return;
    setDeletingPlatformId(idPlatform);
    setLockersIsLoading({ ...lockersIsLoading, delete: true });
    const signal = new AbortController().signal;
    const response = await saveDeallocatePlatform(signal, { platformId: idPlatform, clientId: IdCliente });
    if (response) {
      if (response?.code !== 200) {
        setModal({
          title: translate("global.alert.attention"),
          body: translate("dashboard.packages.details.dropOff.receivePackage.NOT_FOUND"),
          footer: <Button variant="primary" onClick={() => setShowModal(false)}>{translate('global.ok')}</Button>,
        });
        setShowModal(true);
      }
      getLockersData();
    }
    setLockersIsLoading({ ...lockersIsLoading, delete: false });
  };

  const handleSavePlatform = async (idPlatform) => {
    if (!canUpdateEffective) return;
    setLockersIsLoading({ ...lockersIsLoading, save: true });
    const signal = new AbortController().signal;
    const boxitLocationIds = selectedPlatforms.map(location => location?.boxitLocationId);

    const response = await saveAssignedPlatform(signal, { platformId: idPlatform, clientId: IdCliente, platformsSelected: boxitLocationIds });
    if (response) {
      if (response?.code !== 200) {
        setModal({
          title: translate("global.alert.attention"),
          body: translate("dashboard.packages.details.dropOff.receivePackage.NOT_FOUND"),
          footer: <Button variant="primary" onClick={() => setShowModal(false)}>{translate('global.ok')}</Button>,
        });
        setShowModal(true);
      }
      getLockersData();
    }
    setLockersPlatformSelect(null);
    setSelectedPlatforms([]);
    setLockersIsLoading({ ...lockersIsLoading, save: false });
  };

  const handleResetValueSearch = () => {
    handleSearchBoxFilter('');
    setLockersSearchValue('');
  };

  const handleSearchBoxFilter = (value) => {
    const normalizeString = (str) => {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
    };

    const filteredPlatforms = lockersData.filter((platform) =>
      normalizeString(platform?.NamePlatform)?.toLowerCase().includes(normalizeString(value)?.toLowerCase())
    );
    setLockersDataFiltered(filteredPlatforms);
    setLockersSearchValue(value);
  };

  if (!client) return null;

  const handleStartEditPrimary = () => {
    if (!canUpdateEffective) return;
    setIsEditingPrimary(true);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleCancelEditPrimary = () => {
    setIsEditingPrimary(false);
    setPlatformSelect(null);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleSavePrimary = async () => {
    if (!canUpdateEffective) return;
    if (!platformSelect) {
      setSaveError(translate('dashboard.clients.detail.platformInfo.validation.platformRequired') || 'Locker is required');
      return;
    }

    setSaveError('');
    setSaveSuccess(false);
    setIsSaving(true);

    try {
      const result = await onUpdateClient({
        typeUpdate: 'EDIT_PLATFORM_CLIENT',
        plataforma: platformSelect?.boxitLocationId || platformSelect?.IdPlataforma || client?.IdPlataforma,
        plataformaSec: client?.IdPlataformaSec,
      });

      if (result.success) {
        setSaveSuccess(true);
        setIsEditingPrimary(false);
        setPlatformSelect(null);
        setTimeout(() => setSaveSuccess(false), 3000);
        if (onReloadClient) {
          await onReloadClient({ clientId });
        }
      } else {
        setSaveError(result.error || translate('dashboard.clients.detail.platformInfo.errors.saveError') || 'Error saving');
      }
    } catch (error) {
      setSaveError(error.message || translate('dashboard.clients.detail.platformInfo.errors.saveError') || 'Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditSecondary = () => {
    if (!canUpdateEffective) return;
    setIsEditingSecondary(true);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleCancelEditSecondary = () => {
    setIsEditingSecondary(false);
    setPlatformSelect(null);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleSaveSecondary = async () => {
    if (!canUpdateEffective) return;
    if (!platformSelect) {
      setSaveError(translate('dashboard.clients.detail.platformInfo.validation.platformRequired') || 'Locker is required');
      return;
    }

    setSaveError('');
    setSaveSuccess(false);
    setIsSaving(true);

    try {
      const result = await onUpdateClient({
        typeUpdate: 'EDIT_PLATFORM_SEC_CLIENT',
        plataforma: client?.IdPlataforma,
        plataformaSec: platformSelect?.boxitLocationId || platformSelect?.IdPlataforma || client?.IdPlataformaSec,
      });

      if (result.success) {
        setSaveSuccess(true);
        setIsEditingSecondary(false);
        setPlatformSelect(null);
        setTimeout(() => setSaveSuccess(false), 3000);
        if (onReloadClient) {
          await onReloadClient({ clientId });
        }
      } else {
        setSaveError(result.error || translate('dashboard.clients.detail.platformInfo.errors.saveError') || 'Error saving');
      }
    } catch (error) {
      setSaveError(error.message || translate('dashboard.clients.detail.platformInfo.errors.saveError') || 'Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.overviewContainer}>
      {(saveError || saveSuccess) && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem 1rem', 
          borderRadius: '8px',
          backgroundColor: saveSuccess ? '#d4edda' : '#f8d7da',
          color: saveSuccess ? '#155724' : '#721c24',
          border: `1px solid ${saveSuccess ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {saveSuccess ? (translate('dashboard.clients.detail.platformInfo.messages.saveSuccess') || 'Saved successfully') : `✗ ${saveError}`}
        </div>
      )}

      <div className="flex flex-wrap">
        {/* Primary Locker */}
        <div className="lg:w-6/12 md:w-full mb-4">
          <ContentCard 
            title={
              <div className={styles.cardTitle}>
                <GiPlatform size={20} />
                <span>
                  {!verifyHideCountryItems(["us"]) 
                    ? translate('global.platformPri') 
                    : translate('global.locker')}
                </span>
              </div>
            }
            padding="md"
            className={styles.infoCard}
            // headerActions deshabilitado temporalmente para evitar edición
            // headerActions={
            //   !isEditingPrimary ? (
            //     <Button 
            //       variant="ghostOutline" 
            //       onClick={handleStartEditPrimary}
            //       leftIcon={<FaIcons.FaEdit />}
            //       className={styles.sectionEditButton}
            //     >
            //       {translate('global.edit')}
            //     </Button>
            //   ) : (
            //     <div style={{ display: 'flex', gap: '0.5rem' }}>
            //       <Button 
            //         variant="primary" 
            //         onClick={handleSavePrimary}
            //         leftIcon={<FaIcons.FaSave />}
            //         className={styles.sectionEditButton}
            //         loading={isSaving}
            //         disabled={isSaving || !platformSelect}
            //       >
            //         {translate('global.save')}
            //       </Button>
            //       <Button 
            //         variant="ghostOutline" 
            //         onClick={handleCancelEditPrimary}
            //         className={styles.sectionEditButton}
            //         disabled={isSaving}
            //       >
            //         {translate('global.cancel')}
            //       </Button>
            //     </div>
            //   )
            // }
          >
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>
                  <GiPlatform className={styles.infoIcon} />
                  {translate('global.name')}
                </span>
                {isEditingPrimary ? (
                  <div style={{ flex: 1 }}>
                    <PlatformSearchList
                      register={register}
                      errors={errors}
                      setPlatformSelect={setPlatformSelect}
                      platfSelect={platformSelect}
                      valueId="plataforma"
                      readOnly={false}
                      setValue={setValue}
                      idPlatform={client?.IdPlataforma}
                      handleIsBoxit={setIsBoxit}
                    />
                  </div>
                ) : (
                  <span className={styles.infoValue}>{client?.Plataforma || 'N/A'}</span>
                )}
              </div>
              {verifyHideCountryItems(["us"]) && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    <RiMoneyDollarCircleFill className={styles.infoIcon} />
                    {translate('global.balance')}
                  </span>
                  <span className={styles.infoValue}>
                    {client?.SaldoCliente ? formatter.format(client.SaldoCliente) : '0.00'}
                  </span>
                </div>
              )}
            </div>
          </ContentCard>
        </div>

        {/* Secondary Locker */}
        {!verifyHideCountryItems(["us"]) && client?.IdPlataformaSec && (
          <div className="lg:w-6/12 md:w-full mb-4">
            <ContentCard 
              title={
                <div className={styles.cardTitle}>
                  <GiPlatform size={20} />
                  <span>{translate('global.platformSec')}</span>
                </div>
              }
              padding="md"
              className={styles.infoCard}
              // headerActions deshabilitado temporalmente para evitar edición
              // headerActions={
              //   !isEditingSecondary ? (
              //     <Button 
              //       variant="ghostOutline" 
              //       onClick={handleStartEditSecondary}
              //       leftIcon={<FaIcons.FaEdit />}
              //       className={styles.sectionEditButton}
              //     >
              //       {translate('global.edit')}
              //     </Button>
              //   ) : (
              //     <div style={{ display: 'flex', gap: '0.5rem' }}>
              //       <Button 
              //         variant="primary" 
              //         onClick={handleSaveSecondary}
              //         leftIcon={<FaIcons.FaSave />}
              //         className={styles.sectionEditButton}
              //         loading={isSaving}
              //         disabled={isSaving || !platformSelect}
              //       >
              //         {translate('global.save')}
              //       </Button>
              //       <Button 
              //         variant="ghostOutline" 
              //         onClick={handleCancelEditSecondary}
              //         className={styles.sectionEditButton}
              //         disabled={isSaving}
              //       >
              //         {translate('global.cancel')}
              //       </Button>
              //     </div>
              //   )
              // }
            >
              <div className={styles.infoList}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>
                    <GiPlatform className={styles.infoIcon} />
                    {translate('global.name')}
                  </span>
                  {isEditingSecondary ? (
                    <div style={{ flex: 1 }}>
                      <PlatformSearchList
                        register={register}
                        errors={errors}
                        setPlatformSelect={setPlatformSelect}
                        platfSelect={platformSelect}
                        valueId="plataformaSec"
                        readOnly={false}
                        setValue={setValue}
                        idPlatform={client?.IdPlataformaSec}
                        handleIsBoxit={setIsBoxit}
                      />
                    </div>
                  ) : (
                    <span className={styles.infoValue}>{client?.NombrePlataformaSec || 'N/A'}</span>
                  )}
                </div>
              </div>
            </ContentCard>
          </div>
        )}
      </div>

      {/* Lockers Assigned Card */}
      <div className="flex flex-wrap">
        <div className="w-full mb-4">
          <ContentCard
              title={
                <div className={styles.cardTitle}>
                  <PiLockersFill size={20} />
                  <span>{translate("global.lockersAsigned")}</span>
                  <Badge variant="neutral" size="sm" showIcon={false}>
                    {lockersData.length}
                  </Badge>
                </div>
              }
              padding="md"
              className={styles.infoCard}
              headerActions={
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '300px' }}>
                    <Input
                      type="text"
                      placeholder={translate("global.search")}
                      value={lockersSearchValue}
                      onChange={(e) => handleSearchBoxFilter(e.target.value || '')}
                      rightIcon={lockersSearchValue ? (
                        <span 
                          onClick={handleResetValueSearch} 
                          style={{ cursor: 'pointer', fontSize: '1.2rem', lineHeight: '1', color: '#86868b' }}
                        >
                          ×
                        </span>
                      ) : (
                        <IoSearchOutline size={18} />
                      )}
                      fullWidth
                    />
                  </div>
                  {canUpdateEffective && (
                    <Button
                      variant={lockersEditMode ? "ghostOutline" : "primary"}
                      onClick={() => {
                        setLockersEditMode(!lockersEditMode);
                        if (lockersEditMode) {
                          setLockersPlatformSelect(null);
                          setSelectedPlatforms([]);
                        }
                      }}
                      leftIcon={lockersEditMode ? <FaIcons.FaTimes /> : <FaIcons.FaEdit />}
                      className={styles.sectionEditButton}
                    >
                      {lockersEditMode ? translate("global.close") : translate("global.edit")}
                    </Button>
                  )}
                </div>
              }
            >
              {canUpdateEffective && lockersEditMode && (
                <div style={{ 
                  marginBottom: '1.5rem', 
                  padding: '1.25rem', 
                  backgroundColor: 'var(--bg-secondary, #f5f5f7)', 
                  borderRadius: '8px',
                  border: '1px solid var(--border-light, #d2d2d7)'
                }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <span style={{ 
                      fontSize: '13px', 
                      fontWeight: 600, 
                      color: 'var(--text-primary, #1d1d1f)',
                      display: 'block',
                      marginBottom: '0.5rem'
                    }}>
                      {translate("global.addPlatform")}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '250px' }}>
                      <PlatformSearchList
                        register={register}
                        errors={errors}
                        setPlatformSelect={setLockersPlatformSelect}
                        platfSelect={lockersPlatformSelect}
                        valueId="plataforma"
                        readOnly={false}
                        setValue={setValue}
                        idsToHide={idsHide}
                        isMultiSelect
                        selectedPlatforms={selectedPlatforms}
                        setSelectedPlatforms={setSelectedPlatforms}
                      />
                    </div>
                    {(lockersPlatformSelect || selectedPlatforms.length > 0) && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button
                          variant="primary"
                          onClick={() => handleSavePlatform(lockersPlatformSelect?.boxitLocationId)}
                          disabled={lockersIsLoading.save}
                          loading={lockersIsLoading.save}
                          leftIcon={<FaIcons.FaSave />}
                        >
                          {translate("global.save")}
                        </Button>
                        <Button
                          variant="ghostOutline"
                          onClick={() => {
                            setSelectedPlatforms([]);
                            setLockersPlatformSelect(null);
                          }}
                          disabled={lockersIsLoading.save}
                        >
                          {translate("global.cancel")}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {lockersIsLoading.data ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                  <Spinner type={1} size={40} />
                </div>
              ) : lockersDataFiltered.length > 0 ? (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '0.75rem',
                  padding: '1rem'
                }}>
                  {lockersDataFiltered.map((item, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        transition: 'transform 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!lockersEditMode) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {canUpdateEffective && lockersEditMode && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeletePlatform(item?.IdPlatform)}
                          disabled={lockersIsLoading.delete && deletingPlatformId === item?.IdPlatform}
                          loading={lockersIsLoading.delete && deletingPlatformId === item?.IdPlatform}
                          style={{ 
                            borderRadius: '8px 0 0 8px', 
                            minWidth: 'auto', 
                            padding: '0.5rem 0.75rem',
                            height: '100%'
                          }}
                        >
                          {deletingPlatformId === item?.IdPlatform && lockersIsLoading.delete ? (
                            <Spinner type={2} size={16} />
                          ) : (
                            <MdDeleteForever size={18} />
                          )}
                        </Button>
                      )}
                      <Badge
                        variant="neutral"
                        showIcon={false}
                        style={{ 
                          borderRadius: lockersEditMode ? '0 8px 8px 0' : '8px', 
                          padding: '0.625rem 1rem',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: lockersEditMode ? 'default' : 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {item?.NamePlatform}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem 2rem', 
                  color: 'var(--text-secondary, #86868b)',
                  fontSize: '15px'
                }}>
                  {lockersSearchValue ? (
                    <>
                      {translate('dashboard.clients.detail.lockersTab.noResults') || 'No lockers found matching your search.'}
                      <div style={{ marginTop: '0.5rem' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleResetValueSearch}
                        >
                          {translate('global.clearSearch') || 'Clear search'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    translate('dashboard.clients.detail.lockersTab.empty') || 'No lockers assigned'
                  )}
                </div>
              )}
            </ContentCard>
        </div>
      </div>

      <ModalContent
        title={modal.title}
        body={modal.body}
        footer={modal.footer}
        isOpenModal={showModal}
        onHide={() => setShowModal(false)}
      />
    </div>
  );
};

export default PlatformInfoTab;

